// Modify to work on build server and locally
import * as momentImport from 'moment';
// Use a type assertion to make TypeScript happy
const moment = (momentImport as any).default || momentImport;

import {
  GridDatasourceSort,
  GridDatasourcePage,
  GridDatasourceGroup,
  GridDatasourceResponse,
  GridDatasourceParams,
} from '../../grid-datasource';
import {
  DapiDataRequest,
  DapiDataResponse,
  DateRangeFilter,
  DomainFilter,
  CompareFilter,
  MultiFilter,
  AggregationFilter,
  CompareType,
  AggregationFunction,
  sortMap,
  DapiExportRequest,
  OrFilter,
} from './dapi.model';
import {
  ExportOptions,
  PinnedColumns,
  Sort,
  SortModel,
  RangeFilter,
  FinraFilter,
  ListFilter,
  FilterType,
  FinraFilters,
  BasicFilter,
} from '../../grid-model';
import { AG_GRID_AUTOCOLUMN, UNITS } from '../../grid-constants';

export const EMPTY_VALUE_AG_GRID_PROXY = '_other';
export const NULL_VALUE_AG_GRID_PROXY = '_missing';

const MAX_GROUP_RECORDS = 5000;
const NESTED_GRID_EXPANSION_RECORDS = 1; // DAPI interprets a nested grid drilldown as a single record

export const operatorMap = {
  lessThan: CompareType.LESSER,
  lessThanOrEqual: CompareType.LTE,
  greaterThan: CompareType.GREATER,
  greaterThanOrEqual: CompareType.GTE,
  equals: CompareType.EQUAL,
  notEqual: CompareType.NOT_EQUAL,
  inRange: null,
};

// merge sort and modify any existing ones
export const mergeSort = (listA, listB) => {
  const updatedList = listA.map((colA) => listB.find((colB) => colA.id === colB.id) || colA);

  return Array.from(new Set([...updatedList, ...listB]));
};

export function mapDapiExportParams(
  {
    columns,
    filter = {},
    grouping = [],
    pinnedColumns = {},
    sort,
  }: { columns: any; filter: any; grouping: any[]; pinnedColumns: PinnedColumns; sort: any },
  schemaDict: any,
  exportOptions: ExportOptions,
): DapiExportRequest {
  // Extract top level grid columns (add random prop for array cols because DAPI requires it)
  let topLevelColumns = columns.filter((col) => isTopLevel(col, schemaDict));

  // Ensure pinned column order is maintained
  if (pinnedColumns) {
    if (pinnedColumns.left) {
      pinnedColumns.left.reverse().forEach((col) => {
        const colIndex = topLevelColumns.findIndex((topLevelCol) => topLevelCol === col);
        topLevelColumns.unshift(topLevelColumns.splice(colIndex, 1)[0]);
      });
    }

    if (pinnedColumns.right) {
      pinnedColumns.right.forEach((col) => {
        const colIndex = topLevelColumns.findIndex((topLevelCol) => topLevelCol === col);
        topLevelColumns.push(topLevelColumns.splice(colIndex, 1)[0]);
      });
    }
  }

  // Map top-level cols to the DAPI interface
  topLevelColumns = prepareDisplayColsForExport(topLevelColumns, schemaDict, { includePrimaryKeyField: true });

  // Extract subgrid columns
  let subgridColumns = [];
  if (exportOptions.subgrids) {
    exportOptions.subgrids
      .map((subgrid) => columns.filter((col) => col.startsWith(subgrid) && !isTopLevel(col, schemaDict)))
      .forEach((cols) => subgridColumns.push(...cols));
    subgridColumns = prepareDisplayColsForExport(subgridColumns, schemaDict, { includePrimaryKeyField: false });
  }

  // Map sort fields as required by dapi request
  let mappedSort = [];
  if (grouping.length > 0) {
    // all group columns get a default of asc/+
    const mapGroupColsToSort = grouping.map((id) => ({
      id,
      sort: '+',
    }));
    mappedSort = mergeSort(
      mapGroupColsToSort,
      sort.map((item) => ({ ...item, sort: item.sort === 'asc' ? '+' : '-' })),
    );
  } else {
    mappedSort = sort.map((item) => ({ ...item, sort: item.sort === 'asc' ? '+' : '-' }));
  }

  const groupColumnId = AG_GRID_AUTOCOLUMN;
  const sortModel = mappedSort.filter((x) => x.id !== groupColumnId);

  // Map groups
  const groups = grouping.map((id) => {
    return {
      id,
      displayName: schemaDict[id].displayName,
    };
  });

  const result = {
    aggregationFilter: mapAggregationFilter(filter),
    arrayFields: subgridColumns,
    compareFilters: mapCompareFilters(filter),
    multiFieldMatchFilters: mapMultiFilters(filter),
    dateRangeFilters: mapDateRangeFilters(filter, schemaDict),
    delimiter: null,
    domainFilters: mapDomainFilters(filter),
    fileName: `${exportOptions.fileName}`,
    fileType: exportOptions.fileType,
    groupFields: groups,
    limit: MAX_GROUP_RECORDS,
    mainFields: topLevelColumns,
    offset: 0,
    orFilters: mapOrFilters(filter),
    quoteValues: false,
    sortFields: mapSortFields(sortModel),
    singleFile: !!exportOptions.singleFile,
    zipped: exportOptions.zipped,
  };

  return result;
}

export function mapDapiRequest(
  {
    columns,
    filter = {},
    group = { rowGroupCols: [], groupKeys: [], otherGroup: [] },
    page,
    sort,
  }: { columns: any; filter?: any; group?: GridDatasourceGroup; page?: GridDatasourcePage; sort: GridDatasourceSort[] },
  schemaDict: any,
): DapiDataRequest {
  if (!filter) {
    return null;
  }

  const preparedRowGroupColumns = prepareRowGroupColumns(group);
  const isGroupRequest = checkGroupRequest(group);

  const groupColumnId = AG_GRID_AUTOCOLUMN;
  const groupColumn = sort.find((x) => x.colId === groupColumnId);
  const sortModel = sort
    .filter((x) => x.colId !== groupColumnId)
    .map<Sort>((x: { colId: any; sort: any }) => ({ id: x.colId, sort: x.sort }) as Sort);
  const result = (
    isGroupRequest
      ? {
          fields: [preparedRowGroupColumns[group.groupKeys.length]],
          dateRangeFilters: mapDateRangeFilters(filter, schemaDict),
          domainFilters: mapDomainFilters(filter),
          compareFilters: mapCompareFilters(filter),
          multiFieldMatchFilters: mapMultiFilters(filter),
          orFilters: mapOrFilters(filter),
          aggregationFilter: {
            function: AggregationFunction.COUNT,
            field: '*',
          },
          sortFields: mapSortFieldsWhenGrouped(groupColumn, preparedRowGroupColumns[group.groupKeys.length], sortModel),
          limit: MAX_GROUP_RECORDS,
          offset: 0,
          delimiter: null,
          quoteValues: false,
        }
      : {
          fields: columns.map((x) => x.id),
          dateRangeFilters: mapDateRangeFilters(filter, schemaDict),
          domainFilters: mapDomainFilters(filter),
          compareFilters: mapCompareFilters(filter),
          multiFieldMatchFilters: mapMultiFilters(filter),
          orFilters: mapOrFilters(filter),
          aggregationFilter: mapAggregationFilter(filter),
          sortFields: mapSortFields(sortModel),
          limit: page ? page.endRow - page.startRow : NESTED_GRID_EXPANSION_RECORDS,
          offset: page ? page.startRow : 0,
          delimiter: null,
          quoteValues: false,
        }
  ) as DapiDataRequest;

  if (group.groupKeys && group.groupKeys.length > 0) {
    const replacedGroupKeys = replaceGroupKeyPlaceholders(group.groupKeys);

    for (let i = 0; i < replacedGroupKeys.length; i++) {
      if (preparedRowGroupColumns[i] === 'issuerName') {
        result.multiFieldMatchFilters.push({
          fuzzy: false,
          searchValue: replacedGroupKeys[i],
          synonym: true,
        });
      } else {
        result.compareFilters.push({
          fieldName: preparedRowGroupColumns[i],
          fieldValue: replacedGroupKeys[i],
          compareType: CompareType.EQUAL,
        });
      }
    }
  }

  return result;
}

export function mapDapiResponse(
  { group = { rowGroupCols: [], groupKeys: [], otherGroup: [] }, page }: Partial<GridDatasourceParams>,
  resp: DapiDataResponse,
): GridDatasourceResponse {
  const rowGroupCols = prepareRowGroupColumns(group);
  const isGroupRequest = checkGroupRequest(group);

  const totalRecords = parseInt(resp.returnBody.headers['Record-Total'], 10);

  const gridData: GridDatasourceResponse = {
    data: resp.returnBody.data ? JSON.parse(resp.returnBody.data) : [],
    totalRecords,
  };

  // for group requests, return the data for the current group
  if (isGroupRequest) {
    // response to a grouping request should be in this format:
    // {key: {lastName: "aabel"}, aggregationResult: 1}
    // {key: {firstName: "david", lastName: "smith"}, aggregationResult: 2}
    const groupingLevel = group.groupKeys.length;
    const groupId = rowGroupCols[groupingLevel];

    // 1. map the response into the format ag-grid needs
    // TODO, we should be able to combine the mapping for #1 & #2
    let groupData = gridData.data
      .slice(page.startRow, page.endRow)
      .map((val) => ({ [groupId]: val[groupId], count: val._COUNT }));
    const currentGroupIndex = group.groupKeys && group.groupKeys.length > 0 ? group.groupKeys.length - 1 : 0;

    // 2. convert the response into a nested structure when the grouping level is a dotted prop path
    const transformedGroupData = groupData.map((row) => {
      return groupId.split('.').reduceRight((val, current, index, path) => {
        let pathNode;
        if (index === path.length - 1) {
          // when it's the last item in the path, compute the label
          // (empties and null values are handled in a special way)
          pathNode = {
            [current]:
              row[groupId] === '' ? EMPTY_VALUE_AG_GRID_PROXY : !row[groupId] ? NULL_VALUE_AG_GRID_PROXY : row[groupId],
          };
        } else {
          pathNode = {
            [current]: val,
          };
        }

        // when we've reached the top of the path, add the count
        if (index === 0) {
          pathNode.count = row.count;
        }

        return pathNode;
      }, null);
    });

    gridData.data = transformedGroupData;
  }

  return gridData;
}

// export function mapFields(cols: { id: string, displayName: string }[]): string[] {
//     return cols.map(col => col.id);
// }

export function paramsBy<T>({ filterModel, fn }: { filterModel: any; fn: (x: string, y: any) => T | T[] }): T[] {
  if (!filterModel) {
    return null;
  }

  const result: T[] = [];

  // tslint:disable-next-line: forin
  for (const key in filterModel) {
    const obj = fn(key, filterModel[key]);

    if (obj != null) {
      result.push(...(Array.isArray(obj as T[]) ? (obj as T[]) : [obj as T]));
    }
  }

  return result;
}

export function mapDateRangeFilters(filterModel: any, schemaDict: any): DateRangeFilter[] {
  return paramsBy<DateRangeFilter>({
    filterModel,
    fn: (key, x) => {
      if (getDapiFilterType(x) !== 'dateRange') {
        return null;
      }

      let filter: any = {
        fieldName: key,
      };

      if ((x as RangeFilter).type === 'relative') {
        filter = {
          ...filter,
          ...getRelativeToExactDateTimeRange(x, schemaDict[key]),
        };
      } else {
        filter = {
          ...filter,
          startDate: (x as RangeFilter).filter,
          endDate: (x as RangeFilter).filterTo,
        };
      }
      return filter;
    },
  });
}
/**
 * RAMPDO-2924 Add timestamp for FINPRO fields converted from relative to exact
 */
export function getRelativeToExactDateTimeRange(filterModel, colDef: any) {
  const pattern = filterModel.pattern ? filterModel.pattern : colDef.pattern;
  const exactRange = calculateExactDatesFromRelativeRange(filterModel.filter);
  const hasExtendedDatePattern = pattern && pattern === 'yyyy-MM-dd HH:mm:ss.SSS';
  if (filterModel.filterType === 'date-time') {
    exactRange.startDate = !hasExtendedDatePattern
      ? `${exactRange.startDate} 00:00:00`
      : `${exactRange.startDate} 00:00:00.000`;
    exactRange.endDate = !hasExtendedDatePattern
      ? `${exactRange.endDate} 23:59:59`
      : `${exactRange.endDate} 23:59:59.999`;
  }
  return exactRange;
}

export function calculateExactDatesFromRelativeRange(value) {
  const regexp = /(-?\d+)+([dmqwy])/;
  const matches = value.match(regexp);
  const count = parseInt(matches[1], 10); // the number of periods (-3, 3, etc)
  const unit = matches[2]; // represents days(d), months(m), quarters(q), weeks (w), years(y)
  const FORMAT = 'YYYY-MM-DD';

  if (count === 0) {
    // If count is 0, return current(d,w,m,q,y) start and end dates
    return {
      startDate: moment().startOf(UNITS[unit]).format(FORMAT),
      endDate: moment().endOf(UNITS[unit]).format(FORMAT),
    };
  } else if (count < 0) {
    // If operator is -, return past (d,w,m,q,y) start and end dates
    return {
      startDate: moment().add(count, UNITS[unit]).startOf(UNITS[unit]).format(FORMAT),
      endDate: moment().subtract(1, UNITS[unit]).endOf(UNITS[unit]).format(FORMAT),
    };
  } else {
    // If operator is +, return future (d,w,m,q,y) start and end dates
    return {
      startDate: moment().add(1, UNITS[unit]).startOf(UNITS[unit]).format(FORMAT),
      endDate: moment().add(count, UNITS[unit]).endOf(UNITS[unit]).format(FORMAT),
    };
  }
}

export function getDapiFilterType(filterModel: FinraFilter | FinraFilter[]) {
  if ((filterModel as ListFilter).filterList) {
    return 'domain';
  }

  if (
    ((filterModel as BasicFilter | RangeFilter).filterType === FilterType.DATE ||
      (filterModel as BasicFilter | RangeFilter).filterType === FilterType.DATE_TIME) &&
    ((filterModel as RangeFilter).filterTo || (filterModel as RangeFilter).type === 'relative')
  ) {
    return 'dateRange';
  }

  if (Array.isArray(filterModel)) {
    return 'or';
  }

  return 'compare';
}

export function mapDomainFilters(filterModel: FinraFilters): DomainFilter[] {
  return paramsBy<DomainFilter>({
    filterModel,
    fn: (key, x) => {
      if (getDapiFilterType(x) !== 'domain') {
        return null;
      }
      return {
        fieldName: key,
        values: (x as ListFilter).filterList,
      };
    },
  });
}

export function mapCompareFilter(key, x: FinraFilter): CompareFilter[] {
  if (getDapiFilterType(x) !== 'compare') {
    return null;
  }
  if (key === 'issuerName') {
    return null;
  } else {
    const compareFilter =
      x.filterType === FilterType.NUMBER && (x as RangeFilter).filterTo
        ? [
            {
              fieldName: key,
              fieldValue: (x as RangeFilter).filter,
              compareType: CompareType.GTE,
            },
            {
              fieldName: key,
              fieldValue: (x as RangeFilter).filterTo,
              compareType: CompareType.LTE,
            },
          ]
        : [
            {
              fieldName: key,
              fieldValue: (x as BasicFilter).filter,
              compareType: operatorMap[x.type],
            },
          ];

    return compareFilter;
  }
}

export function mapMultiFilters(filterModel: FinraFilters): MultiFilter[] {
  return paramsBy<MultiFilter>({
    filterModel,
    fn: mapMultiFilter,
  });
}

export function mapMultiFilter(key, x: FinraFilter): MultiFilter[] {
  if (key !== 'issuerName') {
    return null;
  } else {
    const multiFilter = [
      {
        fuzzy: false,
        searchValue: (x as BasicFilter).filter,
        synonym: true,
        fields: [
          {
            name: key,
            boost: 1,
          },
        ],
      },
    ];

    return multiFilter;
  }
}

/*
[{"compareType":"GREATER","fieldValue":"2019-07-01","fieldName":"completedExams.examStatusDate"}
*/
export function mapCompareFilters(filterModel: FinraFilters): CompareFilter[] {
  return paramsBy<CompareFilter>({
    filterModel,
    fn: mapCompareFilter,
  });
}

export function mapOrFilters(filterModel: FinraFilters): OrFilter[] {
  return paramsBy<OrFilter>({
    filterModel,
    fn: (key, x: FinraFilter[]) => {
      if (getDapiFilterType(x) !== 'or') {
        return null;
      }

      const allCompareFilters = [];
      for (const f of x as FinraFilter[]) {
        const filters = mapCompareFilter(key, f);
        if (filters) {
          for (const filter of filters) {
            allCompareFilters.push(filter);
          }
        }
      }

      const orFilter: OrFilter[] = [
        {
          compareFilters: allCompareFilters,
        },
      ];

      return orFilter;
    },
  });
}

export function mapAggregationFilter(filterModel: any): AggregationFilter {
  return null;
}

export function mapSortFields(sortModel: SortModel) {
  return sortModel.map((x) => `${x.sort}${x.id}`);
}

export function mapSortFieldsWhenGrouped(groupColumn, field, sortModel: SortModel) {
  // Sort fields based on selected grouped column
  if (groupColumn) {
    return [sortMap[groupColumn.sort.toLowerCase()] + field];
  }
  // Sort fields based on any other column
  if (sortModel && sortModel.length) {
    return sortModel.map((x) => sortMap[x.sort] + x.id);
  }
  // Send empty sort fields for 'no sort' ag-grid sorting order
  return [];
}

export function prepareRowGroupColumns(group: any = { rowGroupCols: [], groupKeys: [] }): string[] {
  let rowGroupCols;

  if (group.rowGroupCols.length > 0) {
    rowGroupCols = group.rowGroupCols.map((col) => col.id);
  }

  const isNoopGroupingRequest =
    rowGroupCols && rowGroupCols.length > 1 && (!group.groupKeys || group.groupKeys.length === 0);

  if (isNoopGroupingRequest) {
    rowGroupCols = rowGroupCols.slice(0, 1);
  }

  return rowGroupCols;
}

/**
 * Prepares the displayed columns for submission to the datasource.
 *
 * Adds any default display columns that must be included for the datasource.
 *
 * For array typed display columns, adds a random property of the array model so that the
 * backend returns the array. (this is kind of a hack that we'd like to move to the backend)
 */
export function prepareDisplayColsForExport(
  cols,
  dict,
  options: { includePrimaryKeyField?: boolean; hideArrayRoot?: boolean; prefix?: string },
): { id: string; displayName: string; include: boolean }[] {
  const defaultOptions = {
    includePrimaryKeyField: true,
    hideArrayRoot: false,
  };

  options = { ...defaultOptions, ...options };

  const result = [];
  const dictKeys = Object.keys(dict);
  const prefix = options.prefix ? options.prefix + '.' : '';

  // for array type display cols, add a random array sub-prop to the display cols as needed
  cols.forEach((col) => {
    const isArray = dict[col] && dict[col].type === 'array';

    if (isArray) {
      // when a sub-prop already exists in the list of display cols, we don't need to add another
      if (cols.find((mappedCol) => mappedCol.indexOf(`${col}.`) === -1)) {
        // filter out just the sub-props of the array column
        const filteredKeys = dictKeys.filter((key) => key.indexOf(`${col}.`) > -1);

        // get the first non-array sub-prop
        let randomSubProp: string;
        let i = 0;
        while (!randomSubProp && i < filteredKeys.length) {
          if (dict[filteredKeys[i]].type !== 'array') {
            randomSubProp = filteredKeys[i];
          }
          i++;
        }
        // add the sub-prop to the list of requested display cols
        if (randomSubProp) {
          result.push({
            id: `${prefix}${randomSubProp}`,
            displayName: dict[randomSubProp].displayName,
            include: false,
            primary: !!(dict[randomSubProp] && dict[randomSubProp].primaryKey),
          });
        }
      }
    }

    if (!isArray || !options.hideArrayRoot) {
      let field: any = {
        id: `${prefix}${col}`,
        displayName: dict[col].displayName,
        include: true,
      };

      if (!isArray) {
        field = { ...field, primary: !!(dict[col] && dict[col].primaryKey) };
      }
      result.push(field);
    }
  });

  // add the primary key field so that it's always available (e.g. for subsequent master/detail requests)
  if (options.includePrimaryKeyField) {
    const primaryKeyFields = Object.keys(dict).filter((key) => dict[key].primaryKey);

    primaryKeyFields.forEach((pkField) => {
      if (!cols.includes(pkField)) {
        result.push({
          id: pkField,
          displayName: dict[pkField].displayName,
          primary: true,
          include: true,
        });
      }
    });
  }

  return result;
}

/**
 * TODO: This is used by getRows and should be removed in future
 */
export function prepareDisplayCols(
  cols,
  dict,
  options: {
    includeEntityKeyFields?: boolean;
    includePrimaryKeyField?: boolean;
    hideArrayRoot?: boolean;
    prefix?: string;
  },
): { id: string; displayName: string }[] {
  const defaultOptions = {
    includeEntityKeyFields: true,
    includePrimaryKeyField: true,
    hideArrayRoot: false,
  };

  options = { ...defaultOptions, ...options };

  const result = [];
  const dictKeys = Object.keys(dict);
  const prefix = options.prefix ? options.prefix + '.' : '';

  // for array type display cols, add a random array sub-prop to the display cols as needed
  cols.forEach((col) => {
    const isArray = dict[col.id] && dict[col.id].type === 'array';

    if (isArray) {
      // when a sub-prop already exists in the list of display cols, we don't need to add another
      if (cols.find((mappedCol) => mappedCol.id.indexOf(`${col.id}.`) === -1)) {
        // filter out just the sub-props of the array column
        const filteredKeys = dictKeys.filter((key) => key.indexOf(`${col.id}.`) > -1);

        // get the first non-array sub-prop
        let randomSubProp: string;
        let i = 0;
        while (!randomSubProp && i < filteredKeys.length) {
          if (dict[filteredKeys[i]].type !== 'array') {
            randomSubProp = filteredKeys[i];
          }
          i++;
        }
        // add the sub-prop to the list of requested display cols
        if (randomSubProp) {
          result.push({
            id: `${prefix}${randomSubProp}`,
            displayName: dict[randomSubProp].displayName,
          });
        }
      }
    }

    if (!isArray || !options.hideArrayRoot) {
      result.push({
        id: `${prefix}${col.id}`,
        displayName: col.displayName,
      });
    }
  });

  const additionalFields = [];

  // add the primary key field so that it's always available (e.g. for subsequent master/detail requests)
  if (options.includePrimaryKeyField) {
    additionalFields.push(...Object.keys(dict).filter((key) => dict[key].primaryKey));
  }

  if (options.includeEntityKeyFields) {
    /**
     * Entity actions require certain fields to always be available. For example, a link action might need
     * need a firmCrdNumber and a branchCrdNumber to navigate to the intended location. Even when the
     * user hasn't chosen to make firmCrdNumber and branchCrdNumber visible in the grid, we still need
     * those fields so that the entity action can be executed. This logic ensures that the request
     * contains all fields required by entity actions.
     */
    Object.keys(dict).forEach((key) => {
      const item = dict[key];
      if (item && item.entity) {
        Object.keys(item.entity).forEach((prop) => {
          if (prop !== 'id') {
            additionalFields.push(item.entity[prop]);
          }
        });
      }
    });
  }

  // add any additional fields not already part of the display cols
  [...new Set(additionalFields)].forEach((field) => {
    if (!cols.find((col) => col && col.id === field)) {
      result.push({
        id: field,
        displayName: dict[field].displayName,
      });
    }
  });

  return result;
}

export function replaceGroupKeyPlaceholders(groupKeys: string[]) {
  return groupKeys.map((key) =>
    key === EMPTY_VALUE_AG_GRID_PROXY ? '' : key === NULL_VALUE_AG_GRID_PROXY ? null : key,
  );
}

export function checkGroupRequest({ rowGroupCols, groupKeys } = { rowGroupCols: [], groupKeys: [] }): boolean {
  return (
    (rowGroupCols && rowGroupCols.length === 1 && (!groupKeys || groupKeys.length === 0)) ||
    (rowGroupCols && rowGroupCols.length > 1 && groupKeys && groupKeys.length < rowGroupCols.length)
  );
}

/**
 * Is the passed key a top level type (not an array).
 *
 * Specifically, any top-level prop or prop of a top level object
 *
 * These would all be top-level props:
 *   crdNumber
 *   address.city
 *
 * These would not (given that exams, firms, and registrations are arrays):
 *    registrations.type
 *    exams.score
 *    firms.address.city
 *
 * @param key the field or prop name
 */
export function isTopLevel(key: string, dict: { [key: string]: any }, ignoreInternalUseFields = true) {
  if (key.startsWith('_')) {
    return true;
  } else {
    const propPath = key.split('.');
    // walk the prop tree looking for array type props
    let topLevel = true;
    let i = 0;
    let prop = '';
    while (topLevel && propPath.length > 1 && i < propPath.length) {
      prop += i > 0 ? `.${propPath[i]}` : propPath[i];
      topLevel = dict[prop].type !== 'array';
      i++;
    }
    return topLevel;
  }
}
